import type {
  DisputeType,
  Lien,
  LienArgument,
  LienFile,
  LienValidation,
  RequestToVacate,
  RequestToReopen,
  LienDirectoryEntry,
} from "pages/liens/LienTypes";

import { api } from "./api";
import type { PaginatedResponse } from "types/DataTypes";

type TriggerOCRLienArgs = {
  lienId: string | number;
  data: unknown;
};

type TriggerLienFileOCRArgs = {
  lienFileId: string | number;
  data: unknown;
};

type GetLienAuditHistoryArgs = {
  lienId: string | number;
};

// lien file endpoints
export const lienFileApi = api.injectEndpoints({
  endpoints: (build) => ({
    getLienFileById: build.query<LienFile, { fileId: string }>({
      query: ({ fileId }) => ({
        url: `/api/v1/lien-files/${fileId}/`,
        method: "GET",
      }),
      providesTags: ["LienFile"],
    }),
    patchLienFileById: build.mutation<
      LienFile,
      { fileId: string; data: Partial<LienFile> }
    >({
      query: ({ fileId, data }) => ({
        url: `/api/v1/lien-files/${fileId}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["LienFile"],
    }),
    triggerOCRLien: build.mutation<unknown, TriggerOCRLienArgs>({
      query: ({ lienId, data }) => ({
        url: `/api/v1/liens/${lienId}/trigger-ocr/`,
        method: "POST",
        body: data,
      }),
    }),
    triggerLienFileOCR: build.mutation<unknown, TriggerLienFileOCRArgs>({
      query: ({ lienFileId, data }) => ({
        url: `/api/v1/lien-files/${lienFileId}/trigger-ocr/`,
        method: "POST",
        body: data,
      }),
    }),
    generateLienFileDocument: build.mutation<
      {
        id: number;
        client: string;
        employer: string;
        claimants: string;
        plan_coverages: string;
        liens: string;
        next_payment_date: string;
        total_amount_disputed: number;
        total_amount_to_pay: number;
      },
      {
        fileId: string;
        data: {
          id: number;
          lien_ids?: number[];
          document_type?: string;
        };
      }
    >({
      query: ({ fileId, data }) => ({
        url: `/api/v1/lien-files/${fileId}/generate_document/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["LienFile"],
    }),
  }),
  overrideExisting: false,
});

// lien endpoints
export const lienApi = api.injectEndpoints({
  endpoints: (build) => ({
    getLiens: build.query<PaginatedResponse<Lien>, { fileId: string }>({
      query: ({ fileId }) => ({
        url: `api/v1/liens/?file_id=${fileId}&include_validations=true`,
        method: "GET",
      }),
      providesTags: ["Liens"],
    }),
    getLienValidationById: build.query<LienValidation, { lienId: string }>({
      query: ({ lienId }) => ({
        url: `api/v1/liens/${lienId}/validate/`,
        method: "GET",
      }),
      providesTags: ["LienValidation"],
    }),

    getLienById: build.query<Lien, { lienId: string }>({
      query: ({ lienId }) => ({
        url: `/api/v1/liens/${lienId}/`,
        method: "GET",
      }),
      providesTags: (result, error, { lienId }) => [
        { type: "Lien", id: lienId },
        "Lien",
      ],
    }),

    patchLienById: build.mutation<
      Lien,
      { lienId: string; data: Partial<Lien> }
    >({
      query: ({ lienId, data }) => ({
        url: `/api/v1/liens/${lienId}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Lien", "LienValidation", "LienFile", "Liens"],
    }),

    createLien: build.mutation<
      Lien,
      {
        data: {
          part_type: string;
          date_of_injury: string;
          lien_file: string;
          case_number: string;
        };
      }
    >({
      query: ({ data }) => ({
        url: `api/v1/liens/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Lien", "Liens"],
    }),

    getLienArguments: build.query<
      PaginatedResponse<LienArgument>,
      { lienId: string }
    >({
      query: ({ lienId }) => ({
        url: `/api/v1/lien-arguments/?lien=${lienId}`,
      }),
      providesTags: ["Lien"],
    }),

    createLienArgument: build.mutation<
      LienArgument,
      {
        data: {
          lien: number;
          argument_type: DisputeType;
          payment_history?: number;
          level_of_appeal?: string;
          dispute_all_matching_codes?: boolean;
        };
      }
    >({
      query: ({ data }) => ({
        url: "/api/v1/lien-arguments/",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async ({ data }, { dispatch, queryFulfilled }) => {
        const tempId = `temp-${Date.now()}`;

        const tempArgument: LienArgument = {
          id: tempId as unknown as number,
          lien: data.lien,
          argument_type: data.argument_type,
          payment_history: data.payment_history,
          modified: new Date().toISOString(),
          created: new Date().toISOString(),
          organization: 0,
          total: 0,
        };

        const patchResult = dispatch(
          lienApi.util.updateQueryData(
            "getLienArguments",
            { lienId: data.lien.toString() },
            (draft) => {
              draft.results.push(tempArgument);
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          patchResult.undo();
        }
      },
      invalidatesTags: ["Lien", "PaymentHistories", "LienFile"],
    }),

    updateLienArgument: build.mutation<
      LienArgument,
      { id: number; data: Partial<LienArgument> }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1/lien-arguments/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Lien", "LienFile", "LienValidation"],
    }),

    deleteLienArgument: build.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/api/v1/lien-arguments/${id}/`,
        method: "DELETE",
      }),
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        const getLienArgumentsResult = dispatch(
          lienApi.util.updateQueryData(
            "getLienArguments",
            { lienId: "" },
            (draft) => {
              const argumentToDelete = draft.results.find(
                (arg) => arg.id === id,
              );
              if (argumentToDelete) {
                const index = draft.results.indexOf(argumentToDelete);
                if (index !== -1) {
                  draft.results.splice(index, 1);
                }
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          getLienArgumentsResult.undo();
        }
      },
      invalidatesTags: [
        "Lien",
        "PaymentHistories",
        "LienFile",
        "LienValidation",
      ],
    }),

    deleteLienById: build.mutation<void, { lienId: string }>({
      query: ({ lienId }) => ({
        url: `/api/v1/liens/${lienId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Liens", "LienFile"],
    }),

    getRequestToVacate: build.query<
      PaginatedResponse<RequestToVacate>,
      {
        lienId: number;
      }
    >({
      query: ({ lienId }) => ({
        url: `/api/v1/request-to-vacate/?lien=${lienId}`,
        method: "GET",
      }),
      providesTags: ["RequestToVacate"],
    }),

    createRequestToVacate: build.mutation<
      RequestToVacate,
      {
        data: RequestToVacate;
      }
    >({
      query: ({ data }) => ({
        url: "/api/v1/request-to-vacate/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["RequestToVacate"],
    }),

    patchRequestToVacate: build.mutation<
      RequestToVacate,
      {
        id: number;
        data: Partial<RequestToVacate>;
      }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1/request-to-vacate/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["RequestToVacate"],
    }),

    deleteRequestToVacate: build.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/api/v1/request-to-vacate/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["RequestToVacate"],
    }),

    getRequestToReopen: build.query<
      PaginatedResponse<RequestToReopen>,
      { lienId: number }
    >({
      query: ({ lienId }) => ({
        url: `/api/v1/request-to-reopen/?lien=${lienId}`,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({
                type: "RequestToReopen" as const,
                id,
              })),
              { type: "RequestToReopen", id: "LIST" },
            ]
          : [{ type: "RequestToReopen", id: "LIST" }],
    }),

    createRequestToReopen: build.mutation<
      RequestToReopen,
      { data: Omit<RequestToReopen, "id"> }
    >({
      query: ({ data }) => ({
        url: "/api/v1/request-to-reopen/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "RequestToReopen", id: "LIST" }],
    }),

    patchRequestToReopen: build.mutation<
      RequestToReopen,
      { id: number; data: Partial<RequestToReopen> }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1/request-to-reopen/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "RequestToReopen", id },
      ],
    }),

    deleteRequestToReopen: build.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/api/v1/request-to-reopen/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "RequestToReopen", id: "LIST" }],
    }),

    getLienDirectoryEntries: build.query<
      PaginatedResponse<LienDirectoryEntry>,
      {
        limit?: number;
        offset?: number;
        searchTerm?: string;
      }
    >({
      query: ({ limit, offset, searchTerm }) => ({
        url: `api/v1/lien-directory-entries/`,
        method: "GET",
        params: {
          ...(limit !== undefined && { limit: limit.toString() }),
          ...(offset !== undefined && { offset: offset.toString() }),
          ...(searchTerm && { search: searchTerm }),
        },
      }),
      providesTags: ["LienDirectory"],
    }),

    getLienAuditHistory: build.query<unknown, GetLienAuditHistoryArgs>({
      query: ({ lienId }) => ({
        url: `api/v1/liens/${lienId}/audit_trail/`,
        method: "GET",
      }),
      providesTags: ["Liens"],
    }),

    generateLienDocument: build.mutation<
      {
        task_id: string;
        task_status: string;
      },
      {
        lienId: string;
        data: {
          document_type: string;
          level_of_appeal?: string;
        };
      }
    >({
      query: ({ lienId, data }) => ({
        url: `/api/v1/liens/${lienId}/generate_document/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Liens"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetLienFileByIdQuery,
  useGenerateLienFileDocumentMutation,
  useTriggerOCRLienMutation,
  usePatchLienFileByIdMutation,
  useTriggerLienFileOCRMutation,
} = lienFileApi;

export const {
  useGetLienValidationByIdQuery,
  useGetLienByIdQuery,
  usePatchLienByIdMutation,
  useGetLiensQuery,
  useCreateLienMutation,
  useGetLienArgumentsQuery,
  useCreateLienArgumentMutation,
  useUpdateLienArgumentMutation,
  useDeleteLienArgumentMutation,
  useDeleteLienByIdMutation,
  useCreateRequestToVacateMutation,
  useGetRequestToVacateQuery,
  usePatchRequestToVacateMutation,
  useDeleteRequestToVacateMutation,
  useGetRequestToReopenQuery,
  useCreateRequestToReopenMutation,
  usePatchRequestToReopenMutation,
  useDeleteRequestToReopenMutation,
  useGetLienDirectoryEntriesQuery,
  useGetLienAuditHistoryQuery,
  useGenerateLienDocumentMutation,
} = lienApi;
